import { useEffect, useRef } from 'react';

export type PromoteAction = 'row' | 'paragraph' | 'copy';

interface Props {
  /** Wrapper holding the trigger and this menu, so a click on the trigger
   *  closes rather than reopens. */
  containerRef: React.RefObject<HTMLElement | null>;
  onPick: (action: PromoteAction) => void;
  onClose: () => void;
}

/**
 * The "→ Script" menu. Three ways out of the pane and into the document, each
 * with a plain-English gloss so the difference between a row and a paragraph
 * never has to be remembered.
 */
export default function PromoteMenu({ containerRef, onPick, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const root = containerRef.current ?? ref.current;
      if (root && !root.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, containerRef]);

  return (
    <div
      ref={ref}
      role="menu"
      className="
        absolute right-0 top-[calc(100%+6px)] w-[248px] py-2
        bg-card border border-line rounded-sm
        shadow-flare-lg/30 z-30 animate-rise-subtle
      "
    >
      <p className="px-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        Send to the script
      </p>
      <MenuItem
        label="Insert as script row"
        gloss="Text lands in the Audio cell of a new row. Visual left empty."
        icon={
          <>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M12 5v14" />
          </>
        }
        onClick={() => onPick('row')}
      />
      <MenuItem
        label="Insert as paragraph"
        gloss="Plain text at the cursor. Good for headings and question lists."
        icon={
          <>
            <path d="M4 6h16" />
            <path d="M4 12h16" />
            <path d="M4 18h10" />
          </>
        }
        onClick={() => onPick('paragraph')}
      />
      <div className="h-px bg-line my-1.5 mx-2" />
      <MenuItem
        label="Copy text"
        icon={
          <>
            <rect x="9" y="9" width="12" height="12" rx="2" />
            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
          </>
        }
        onClick={() => onPick('copy')}
      />
    </div>
  );
}

function MenuItem({
  label,
  gloss,
  icon,
  onClick,
}: {
  label: string;
  gloss?: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="w-full text-left px-3 py-1.5 hover:bg-elevated transition-colors duration-150"
    >
      <span className="flex items-center gap-2.5">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="shrink-0 text-faint"
        >
          {icon}
        </svg>
        <span className="text-[13px] text-fg">{label}</span>
      </span>
      {gloss && (
        <span className="block text-[11px] text-faint leading-snug mt-0.5 pl-[24px]">{gloss}</span>
      )}
    </button>
  );
}
